import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import ClientService from '../../services/ClientService';
import MergeFieldsSection from './MergeFieldsSection';
import MergeStudentsSection from './MergeStudentsSection';
import { buildStudentMergeView } from './studentMergePlan';

function ClientMergeDialog({ show, sourceClient, targetClient, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [context, setContext] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [studentMerges, setStudentMerges] = useState({});
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const session = useRef(0);
  const submitting = useRef(false);
  const sourceId = sourceClient && sourceClient.id;
  const targetId = targetClient && targetClient.id;
  useEffect(() => {
    const generation = ++session.current;
    submitting.current = false;
    setContext(null); setSelectedFields({}); setStudentMerges({}); setError(null);
    if (!show) return undefined;
    setPhase('loading');
    ClientService.previewClientMerge(sourceId, targetId).then(result => {
      if (session.current !== generation) return;
      setContext(result);
      if (result.resume) {
        setSelectedFields(result.savedChoices.fieldSelections || {});
        setStudentMerges(result.savedChoices.studentMerges || {});
      }
      setPhase(result.completed ? 'completed' : 'editing');
    }).catch(failure => {
      if (session.current !== generation) return;
      setError(failure); setPhase('loadError');
    });
    return () => { session.current += 1; };
  }, [show, sourceId, targetId, reload]);

  const view = context && buildStudentMergeView(context, studentMerges);
  const locked = phase !== 'editing';
  const handleConfirm = async () => {
    if (!context || locked || view.errors.length || submitting.current) return;
    const generation = session.current;
    submitting.current = true; setPhase('submitting'); setError(null);
    try {
      await onConfirm({ sourceId,
        targetId,
        operation: 'execute',
        contractVersion: 2,
        contextToken: context.contextToken,
        fieldSelections: selectedFields,
        studentMerges });
      if (session.current === generation) onClose();
    } catch (failure) {
      if (session.current !== generation) return;
      setError(failure);
      if (failure.outcome === 'completed') setPhase('completed');
      else if (failure.outcome !== 'notStarted' || failure.code === 'STALE_MERGE_CONTEXT') setPhase('refreshRequired');
      else setPhase('editing');
    } finally {
      if (session.current === generation) submitting.current = false;
    }
  };
  if (!show) return null;
  let submitLabel = 'manage:mergeClients_141';
  if (context && context.resume) submitLabel = 'manage:mergeStudents.resume';
  if (phase === 'submitting') submitLabel = 'manage:mergeStudents.submitting';
  const errorCode = error && (error.code || 'INCOMPLETE_MERGE_CONTEXT');
  return (
    <Modal show onHide={phase === 'submitting' ? () => {} : onClose} size="xl" centered backdrop={phase === 'submitting' ? 'static' : true}>
      <Modal.Header closeButton={phase !== 'submitting'} closeLabel={t('common:close')}>
        <Modal.Title>{t('manage:mergeClients_137')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {phase === 'loading' && <p role="status">{t('manage:mergeStudents.loading')}</p>}
        {error && (
        <div className="alert alert-danger" role="alert">
          {t(`manage:mergeStudents.errors.${errorCode}`, { defaultValue: t('manage:mergeStudents.errors.INCOMPLETE_MERGE_CONTEXT') })}
          {(error.blockers || []).map(item => <div key={`${item.code}-${item.studentAId || ""}-${item.studentBId || ""}`}>{t(`manage:mergeStudents.errors.${item.code}`)}</div>)}
        </div>
        )}
        {['loadError', 'refreshRequired'].includes(phase) && (
        <Button className="manage-teal-button" onClick={() => setReload(n => n + 1)}>
          {t('manage:mergeStudents.reload')}
        </Button>
        )}
        {phase === 'completed' && <p>{t('manage:mergeStudents.completedRefresh')}</p>}
        {context && (
        <>
          <div className="row">
            {[context.clientA, context.clientB].map((client, index) => (
              <div className="col-md-6" key={client.id}>
                <h6>{t(index === 0 ? 'manage:clientAPrimary_138' : 'manage:clientBToBeRemoved_140')}</h6>
                <strong>{client.firstName} {client.lastName}</strong><small className="d-block">ID: {client.id}</small>
              </div>
            ))}
          </div>
          <hr />
          {context.resume && <p role="status">{t('manage:mergeStudents.resumeInfo')}</p>}
          {context.billing && <p>{t('manage:mergeStudents.billingInfo', { count: context.billing.movedEntries })}</p>}
          <fieldset disabled={locked || !!context.resume}>
            <MergeFieldsSection
              clientA={context.clientA}
              clientB={context.clientB}
              selectedFields={selectedFields}
              onFieldSelect={(key, value) => setSelectedFields(prev => ({ ...prev, [key]: value }))}
            />
          </fieldset>
          <MergeStudentsSection
            studentsA={context.studentsA}
            studentsB={context.studentsB}
            blockers={context.blockers}
            decisions={studentMerges}
            disabled={locked || !!context.resume}
            onDecisionChange={(id, decision) => setStudentMerges(prev => ({ ...prev, [id]: decision }))}
          />
        </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="manage-teal-button" onClick={onClose} disabled={phase === 'submitting'}>{t('common:cancel')}</Button>
        <Button className="manage-red-button" onClick={handleConfirm} disabled={locked || !view || view.errors.length > 0}>
          {t(submitLabel)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
ClientMergeDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  sourceClient: PropTypes.object.isRequired,
  targetClient: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default ClientMergeDialog;
